// services/executionCore.ts - Shared execution logic for both chat interface and studio
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AsyncExecution,
  ExecutionProgressEvent,
  StreamConnection,
  AsyncExecutionConfig,
} from '@/types/AsyncExecution';
import { NodeExecutionState } from '@/types/FlowExecution';

// =================== Core Execution Utilities ===================

export const executionUtils = {
  /**
   * Calculate retry delay with exponential backoff
   */
  calculateRetryDelay(attempt: number, config: AsyncExecutionConfig): number {
    const delay = Math.min(
      config.baseRetryDelay * Math.pow(2, attempt - 1),
      config.maxRetryDelay
    );
    const jitter = Math.random() * 0.1 * delay;
    return Math.floor(delay + jitter);
  },

  /**
   * Determine if connection should be retried
   */
  shouldRetryConnection(error: Error, attempts: number, maxAttempts: number): boolean {
    const errorMessage = error.message.toLowerCase();
    
    // Don't retry on specific errors
    if (errorMessage.includes('404') || 
        errorMessage.includes('execution not found') ||
        errorMessage.includes('401') || 
        errorMessage.includes('unauthorized')) {
      return false;
    }
    
    return attempts < maxAttempts;
  },

  /**
   * Create execution from progress event
   */
  createExecutionFromEvent(event: ExecutionProgressEvent, context: any): AsyncExecution {
    return {
      execution_id: event.execution_id,
      status: event.status || 'RUNNING',
      progress: event.progress || 0,
      current_node: event.current_node || null,
      completed_nodes: event.completed_nodes || 0,
      total_nodes: event.total_nodes || 0,
      final_output: event.final_output || null,
      error_message: event.error_message || null,
      started_at: context.started_at || new Date().toISOString(),
      completed_at: event.status === 'COMPLETED' ? new Date().toISOString() : null,
      execution_source: context.execution_source,
      conversation_id: context.conversation_id,
      workspace_id: context.workspace_id,
      assistant_type: context.assistant_type,
      is_temporary: context.is_temporary,
      cleanup_after: context.cleanup_after,
    };
  },
};

// =================== Execution Progress Tracker ===================

export class ExecutionProgressTracker {
  private executions = new Map<string, AsyncExecution>();
  private progressCallbacks = new Map<string, (event: ExecutionProgressEvent) => void>();

  /**
   * Track a new execution
   */
  trackExecution(execution: AsyncExecution): void {
    this.executions.set(execution.execution_id, execution);
  }

  /**
   * Update execution from progress event
   */
  updateFromProgressEvent(event: ExecutionProgressEvent): AsyncExecution | null {
    const execution = this.executions.get(event.execution_id);
    if (!execution) return null;

    // Update execution state
    if (event.type === 'status_update' || event.type === 'execution_finished') {
      if (event.status) execution.status = event.status;
      if (event.progress !== undefined) execution.progress = event.progress;
      if (event.current_node !== undefined) execution.current_node = event.current_node;
      if (event.completed_nodes !== undefined) execution.completed_nodes = event.completed_nodes;
      if (event.total_nodes !== undefined) execution.total_nodes = event.total_nodes;
      if (event.final_output !== undefined) execution.final_output = event.final_output;
      if (event.error_message !== undefined) execution.error_message = event.error_message;
      
      if (event.status === 'COMPLETED' || event.status === 'FAILED' || event.status === 'CANCELLED') {
        execution.completed_at = new Date().toISOString();
      }
    }

    // Trigger callback if registered
    const callback = this.progressCallbacks.get(event.execution_id);
    if (callback) {
      callback(event);
    }

    return execution;
  }

  /**
   * Register progress callback
   */
  onProgress(executionId: string, callback: (event: ExecutionProgressEvent) => void): void {
    this.progressCallbacks.set(executionId, callback);
  }

  /**
   * Get execution by ID
   */
  getExecution(executionId: string): AsyncExecution | undefined {
    return this.executions.get(executionId);
  }

  /**
   * Get all active executions
   */
  getActiveExecutions(): AsyncExecution[] {
    return Array.from(this.executions.values()).filter(e => 
      ['PENDING', 'RUNNING'].includes(e.status)
    );
  }

  /**
   * Clean up completed executions
   */
  cleanupCompletedExecutions(olderThanMinutes: number = 5): number {
    const cutoffTime = Date.now() - (olderThanMinutes * 60 * 1000);
    let cleaned = 0;
    
    for (const [id, execution] of this.executions.entries()) {
      if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(execution.status) && 
          execution.completed_at && 
          new Date(execution.completed_at).getTime() < cutoffTime) {
        this.executions.delete(id);
        this.progressCallbacks.delete(id);
        cleaned++;
      }
    }
    
    return cleaned;
  }

  /**
   * Stop tracking execution
   */
  stopTracking(executionId: string): void {
    this.executions.delete(executionId);
    this.progressCallbacks.delete(executionId);
  }

  /**
   * Clear all tracking
   */
  clear(): void {
    this.executions.clear();
    this.progressCallbacks.clear();
  }
}

// =================== Connection State Manager ===================

export class ConnectionStateManager {
  private connections = new Map<string, StreamConnection>();

  /**
   * Update connection state
   */
  updateConnection(connection: StreamConnection): void {
    this.connections.set(connection.executionId, { ...connection });
  }

  /**
   * Get connection by execution ID
   */
  getConnection(executionId: string): StreamConnection | undefined {
    return this.connections.get(executionId);
  }

  /**
   * Remove connection
   */
  removeConnection(executionId: string): void {
    this.connections.delete(executionId);
  }

  /**
   * Get connection health metrics
   */
  getConnectionHealth(): {
    status: 'healthy' | 'degraded' | 'offline';
    activeConnections: number;
    failedConnections: number;
  } {
    const connections = Array.from(this.connections.values());
    const activeConnections = connections.filter(c => c.status === 'connected').length;
    const failedConnections = connections.filter(c => c.status === 'failed').length;
    
    let status: 'healthy' | 'degraded' | 'offline' = 'healthy';
    if (connections.length === 0) {
      status = 'healthy';
    } else if (failedConnections === connections.length) {
      status = 'offline';
    } else if (failedConnections > 0) {
      status = 'degraded';
    }

    return { status, activeConnections, failedConnections };
  }

  /**
   * Clear all connections
   */
  clear(): void {
    this.connections.clear();
  }
}

// =================== Node Execution State Manager ===================

export class NodeExecutionTracker {
  private nodeStates = new Map<string, Record<string, NodeExecutionState>>();

  /**
   * Update node state from progress event
   */
  updateNodeFromEvent(executionId: string, event: ExecutionProgressEvent): void {
    if (event.type !== 'progress_event' || !event.node_id) return;

    const states = this.nodeStates.get(executionId) || {};
    
    if (event.event_type === 'node_started') {
      states[event.node_id] = {
        status: 'running',
        type: 'unknown',
        label: event.node_label || 'Unknown Node',
        startTime: event.timestamp
      };
    } else if (event.event_type === 'node_completed') {
      const existing = states[event.node_id] || { status: 'pending' };
      states[event.node_id] = {
        ...existing,
        status: 'completed',
        endTime: event.timestamp
      };
    } else if (event.event_type === 'node_error') {
      const existing = states[event.node_id] || { status: 'pending' };
      states[event.node_id] = {
        ...existing,
        status: 'error',
        error: event.error_message || 'Node execution failed',
        endTime: event.timestamp
      };
    }

    this.nodeStates.set(executionId, states);
  }

  /**
   * Get node states for execution
   */
  getNodeStates(executionId: string): Record<string, NodeExecutionState> {
    return this.nodeStates.get(executionId) || {};
  }

  /**
   * Clear node states for execution
   */
  clearNodeStates(executionId: string): void {
    this.nodeStates.delete(executionId);
  }

  /**
   * Clear all node states
   */
  clear(): void {
    this.nodeStates.clear();
  }
}

// =================== Stream Processing Helper ===================

export async function processExecutionStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: ExecutionProgressEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      if (signal?.aborted) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        
        if (trimmedLine.startsWith('data: ')) {
          const eventData = trimmedLine.slice(6);
          
          if (eventData === '[DONE]') {
            return;
          }

          try {
            const data: ExecutionProgressEvent = JSON.parse(eventData);
            onEvent(data);
            
            if (data.type === 'execution_finished' || 
                (data.type === 'status_update' && 
                 data.status && ['COMPLETED', 'FAILED', 'CANCELLED'].includes(data.status))) {
              return;
            }
          } catch (parseError) {
            console.error('Error parsing progress event:', parseError);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}